import React, { useEffect, useMemo, useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import { Button, Chip, Grid, InputAdornment, TextField } from "@mui/material";
import { Add, Search } from "@material-ui/icons";
import { DataGrid } from "@mui/x-data-grid";
import firebase from "../firebase";
import { dataGridSx, palette } from "../theme";
import PageContainer from "../Components/UI/PageContainer";
import PageHeader from "../Components/UI/PageHeader";

export default function GroupsPage() {
  const history = useHistory();
  const signedIn = global.config.uid !== "";
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!signedIn) return;
    const db = firebase.firestore();
    const unsub = db
      .collection("Main")
      .doc(global.config.uid)
      .collection("Groups")
      .orderBy("Date", "desc")
      .onSnapshot((snapshot) => {
        setGroups(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            fullNames: doc.data().fullNames,
            location: doc.data().Location,
            date: doc.data().Date ? doc.data().Date.toDate() : null,
          }))
        );
        setLoading(false);
      });
    return unsub;
  }, [signedIn]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        (g.fullNames || "").toLowerCase().includes(q) ||
        (g.location || "").toLowerCase().includes(q)
    );
  }, [groups, search]);

  const columns = [
    {
      field: "date",
      headerName: "Date",
      width: 140,
      valueFormatter: (params) =>
        params.value
          ? params.value.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
    },
    {
      field: "fullNames",
      headerName: "Kids in Group",
      flex: 2,
      minWidth: 280,
      renderCell: (params) => <span style={{ fontWeight: 700 }}>{params.value}</span>,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 160,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={params.value}
            sx={{ backgroundColor: palette.primarySoft, color: palette.primaryDark }}
          />
        ) : null,
    },
  ];

  if (!signedIn) return <Redirect to="/" />;

  return (
    <PageContainer>
      <PageHeader
        title="Group Sessions"
        subtitle="Sessions with multiple kids, newest first"
        actions={
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => history.push("/group-profile")}
          >
            Add Group Session
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search by kid or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": "Search group sessions" }}
          />
        </Grid>
      </Grid>

      <DataGrid
        autoHeight
        loading={loading}
        rows={rows}
        columns={columns}
        getRowHeight={() => "auto"}
        onRowClick={(params) => history.push("/group-profile?groupID=" + params.id)}
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
        pageSizeOptions={[15, 30, 50]}
        localeText={{ noRowsLabel: "No group sessions yet" }}
        sx={{
          ...dataGridSx,
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #EEF2F6",
            paddingTop: "10px",
            paddingBottom: "10px",
          },
        }}
      />
    </PageContainer>
  );
}
